import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Drawer, IconButton, makeStyles, Tooltip, Typography, useTheme } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { FiEdit } from 'react-icons/fi';

import { ChatList } from '@vyce/core/src/components/Chat/ChatList';
import { ChatData, ChatMessage, ChatUser } from '@vyce/core/src/types';
import { ChatHeader } from '@vyce/core/src/components/Chat/ChatHeader';
import VyceLoader from '@vyce/core/src/components/VyceLoader';
import { ChatUserDetails } from '@vyce/core/src/components/Chat/ChatUserDetails';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { UnreadMessagesContext } from '@vyce/core/src/contexts/unreadMessagesContext';
import { ChatDetails } from '@vyce/core/src/components/Chat/ChatDetails';
import { markMessagesAsReadRequest } from '@vyce/core/src/api/chat';
import { ChatMessageList } from '@vyce/core/src/components/Chat/ChatMessageList';
import { ChatInput } from '@vyce/core/src/components/Chat/ChatInput';
import { ChatSubscriptionUpsellCard } from '@vyce/core/src/components/Chat/ChatSubscriptionUpsellCard';
import { ChatActivateMessagingBanner } from '@vyce/core/src/components/Chat/ChatActivateMessagingBanner';
import { MESSAGING_FILE_ACCEPTS } from '@vyce/core/src/views/ai/constants';
import { ChatNewGroupChatDialog } from '@vyce/core/src/components/Chat/ChatNewGroupChatDialog';
import { ChatAddMembersDialog } from '@vyce/core/src/components/Chat/ChatAddMembersDialog';
import { ConfirmDialog } from '@vyce/core/src/components/ConfirmDialog';
import { ChatForwardMessageDialog } from '@vyce/core/src/components/Chat/ChatForwardMessageDialog';
import { ChatMessageReactionsDialog } from '@vyce/core/src/components/Chat/ChatMessageReactionsDialog';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { ChatDropZone, ChatFolders, ChatSearchField } from '@vyce/core/src/components/Chat/kit';
import messagesLightBackground from '@vyce/core/src/assets/messages-light-background.svg';
import messagesDarkBackground from '@vyce/core/src/assets/messages-dark-background.svg';

import { MESSAGES_PAGE_SIZE, useChatData } from './hooks/useChatData';
import { useChatTranslation } from './hooks/useChatTranslation';

const leftInfoDrawerWidth = 440;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const FILE_INPUT_ACCEPT = Object.entries(MESSAGING_FILE_ACCEPTS)
  .flatMap(([type, extensions]) => [type, ...extensions])
  .join(',');

// A live snapshot holds only the newest page: older pages the user has
// scrolled back to stay below it instead of vanishing with each new message.
const withOlderPages = (snapshot: ChatMessage[], previous: ChatMessage[]) => {
  if (snapshot.length < MESSAGES_PAGE_SIZE || !previous.length) {
    return snapshot;
  }
  const oldest = snapshot[snapshot.length - 1]?.createdAt?.seconds ?? 0;
  const ids = new Set(snapshot.map(message => message.id));
  return [
    ...snapshot,
    ...previous.filter(
      message => !!message.id && !ids.has(message.id) && (message.createdAt?.seconds ?? 0) < oldest
    ),
  ];
};
const headerHeight = 65;
const bottomButtonHeight = 70;

interface Props {
  user: ChatUser;
  companyId: string;
  route: string;
  height?: string;
  messageContainerPaddingBottom?: string;
  isLegend?: boolean;
  // Company has no messaging subscription: only the Vyce Notifications chat, upsell card,
  // disabled input and the activation banner are available
  restrictedMode?: boolean;
  onActivateClick?: () => void;
  canActivate?: boolean;
  // Bump to refetch the chat list from outside (e.g. after the legend admin
  // messages multiple users and lastMessage/ordering change server-side)
  chatsRefreshKey?: number;
  // Translate incoming messages to English and drafts to the worker's language (Legend's Vyce Channel)
  enableTranslation?: boolean;
  // Keeps the composer clear of Laila's orb in the bottom right corner
  launcherClearance?: boolean;
}

export const Messages: React.FC<Props> = ({
  user,
  companyId,
  route,
  height = 'calc(100vh - 64px)',
  messageContainerPaddingBottom = '16px',
  isLegend = false,
  restrictedMode = false,
  onActivateClick,
  canActivate = true,
  chatsRefreshKey = 0,
  enableTranslation = false,
  launcherClearance = false,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { refresh: refreshUnreadCount } = useContext(UnreadMessagesContext);
  const refreshUnreadCountRef = useRef(refreshUnreadCount);
  refreshUnreadCountRef.current = refreshUnreadCount;
  // Incoming message ids already sent to markMessagesAsRead for the open chat
  const markedAsReadIdsRef = useRef<Set<string>>(new Set());
  // Chat whose unread counter was already reset in this visit: the list row and then the
  // full getChat record both arrive as selectedChat, and one reset is enough.
  const counterResetChatIdRef = useRef<string | null>(null);
  // Chat whose first message snapshot has arrived. The chat now opens before its
  // messages, so "No messages yet" must wait for the snapshot or it flashes.
  const [snapshotChatId, setSnapshotChatId] = useState<string | null>(null);
  // Latest snapshot of the open chat, re-checked when the browser tab becomes visible
  const lastSnapshotRef = useRef<{ companyId: string; chatId: string; messages: ChatMessage[] } | null>(
    null
  );

  const {
    getChats,
    chats,
    loadingChats,
    getChat,
    selectedChatId,
    setSelectedChatId,
    selectedChat,
    loadingChat,
    searchedChats,
    searchChats,
    setSearchedChats,
    setSelectedChat,
    updateChatName,
    loadingChatUpdate,
    updateChatAvatar,
    isChatAdmin,
    updateChatReadOnlyStatus,
    listenToMessages,
    messages,
    setMessages,
    withPendingMessages,
    loadMoreMessages,
    setLoadingChat,
    speechToText,
    loadingInput,
    sendMessage,
    documents,
    uploadDocument,
    deleteDocument,
    createGroupChat,
    searchChatUsers,
    addUsersToChat,
    removeUserFromChat,
    goToChat,
    images,
    setDocuments,
    setImages,
    deleteImage,
    uploadImage,
    deleteMessage,
    editMessage,
    messageToDelete,
    setMessageToDelete,
    messageToEdit,
    setMessageToEdit,
    messageToReply,
    setMessageToReply,
    setMessageToForward,
    forwardMessage,
    reactToMessage,
    selectedReactionMessage,
    setSelectedReactionMessage,
    getMessageReactions,
    messageReactions,
    loadingMessageReactions,
    // Pagination
    loadMoreChats,
    hasMoreChats,
    loadingMoreChats,
    searchingChats,
    unreadOnlyFilter,
    toggleUnreadFilter,
  } = useChatData(user, companyId, route, restrictedMode);
  const { translateMessage, translateDraft } = useChatTranslation();
  const { showNotification } = useContext(NotificationContext);
  const history = useHistory();
  const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
  const [chatDetails, setChatDetails] = useState<ChatData | null>(null);
  const { isMobile } = useContext(DeviceContext);
  const [search, setSearch] = useState('');
  const [openNewGroupChatDialog, setOpenNewGroupChatDialog] = useState<boolean>(false);
  const [openAddMembersDialog, setOpenAddMembersDialog] = useState<boolean>(false);
  const [openDeleteMessageDialog, setOpenDeleteMessageDialog] = useState<boolean>(false);
  const [openForwardMessageDialog, setOpenForwardMessageDialog] = useState<boolean>(false);
  const [openReactionsDialog, setOpenReactionsDialog] = useState<boolean>(false);

  // Picked or dropped files: pictures go as images, the rest as documents.
  const attachFiles = (files: File[]) => {
    const tooBig = files.filter(file => file.size > MAX_FILE_BYTES);
    if (tooBig.length) {
      showNotification({
        message: `Files must be under 10 MB: ${tooBig.map(file => file.name).join(', ')}`,
        options: { variant: 'warning' },
      });
    }
    files
      .filter(file => file.size <= MAX_FILE_BYTES)
      .forEach(file => (file.type.startsWith('image/') ? uploadImage(file) : uploadDocument(file)));
  };

  const chatId = useMemo(() => {
    return new URLSearchParams(history.location.search).get('chatId');
  }, [history.location.search]);

  const chatCompanyId = useMemo(() => {
    return new URLSearchParams(history.location.search).get('companyId');
  }, [history.location.search]);

  // Latest incoming texts (messages are newest first), used to detect the worker's language
  const workerLanguageSamples = useMemo(
    () =>
      enableTranslation
        ? messages
            .filter(message => message.senderId !== user.uid && !!message.text?.trim())
            .slice(0, 5)
            .map(message => message.text)
        : [],
    [enableTranslation, messages, user.uid]
  );

  const isPrivate = useMemo(() => {
    return selectedChat?.type === 'private';
  }, [selectedChat]);

  const showInput = useMemo(() => {
    if (user.uid === 'vyce') {
      return true;
    }
    // Admins can write to the Vyce Notifications chat even without a messaging subscription
    if (restrictedMode) {
      return true;
    }
    if (isPrivate && !selectedChat?.readOnly) {
      return true;
    }
    if (selectedChat?.readOnly && isChatAdmin) {
      return true;
    }
    if (selectedChat?.type === 'group' && !selectedChat?.readOnly) {
      return true;
    }
    return false;
  }, [selectedChat, isChatAdmin, isPrivate, restrictedMode]);

  const handleNewGroupChat = () => setOpenNewGroupChatDialog(true);

  const chatListWidth = useMemo(() => {
    // The page pads its sides on phones, so the full width is the parent's, not the viewport's.
    if (isMobile && !selectedChatId) {
      return '100%';
    }

    return '360px';
  }, [isMobile, selectedChatId]);

  const chatListDisplay = useMemo(() => {
    if (isMobile && selectedChatId) {
      return 'none';
    }
    return 'flex';
  }, [isMobile, selectedChatId]);

  const showDetails = useMemo(() => {
    return !!selectedUser || !!chatDetails;
  }, [selectedUser, chatDetails]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    searchChats(value);
  };

  const onChatClick = (chat: ChatData) => {
    setSelectedChatId(chat.id);
    setSelectedUser(null);
    setChatDetails(null);
    history.push(`${route}?chatId=${chat.id}&companyId=${chat.companyId}`);
  };

  const onDetailsClick = (chat: ChatData) => {
    const isPrivateChat = chat.type === 'private';
    const profile = chat.profiles?.find(profile => profile.uid !== user.uid);
    if (isPrivateChat && profile) {
      setSelectedUser(value => (value ? null : profile));
    } else {
      setChatDetails(chat);
    }
  };

  useEffect(() => {
    if (user.uid && companyId) {
      getChats(user.uid);
      setSelectedChatId(null);
      setSelectedChat(null);
      setSelectedUser(null);
      setChatDetails(null);
      setSelectedReactionMessage(null);
    }
  }, [user.uid, companyId]);

  // External refresh trigger: 0 is the initial value, the mount effect above
  // already loads the list, so only react to subsequent bumps.
  useEffect(() => {
    if (!chatsRefreshKey) {
      return;
    }
    if (user.uid && companyId) {
      getChats(user.uid);
    }
  }, [chatsRefreshKey]);

  useEffect(() => {
    const chatId = new URLSearchParams(history.location.search).get('chatId');
    const chatCompanyId = new URLSearchParams(history.location.search).get('companyId');
    setSelectedChatId(chatId || null);
    if (chatId && user.uid && chatCompanyId) {
      // The list already holds the chat: open it from there instead of waiting for getChat.
      const listed = [...searchedChats, ...chats].find(
        chat => chat.id === chatId && (chat.companyId ?? chatCompanyId) === chatCompanyId
      );
      getChat(chatId, user.uid, chatCompanyId, listed);
    }
  }, [history.location.search, user.uid]);

  // Restricted mode: auto-open the Vyce Notifications chat on desktop
  useEffect(() => {
    if (restrictedMode && !isMobile && !chatId && !loadingChats && searchedChats.length) {
      const vyceChat = searchedChats[0];
      history.replace(`${route}?chatId=${vyceChat.id}&companyId=${vyceChat.companyId}`);
    }
  }, [restrictedMode, isMobile, chatId, loadingChats, searchedChats]);

  useEffect(() => {
    if (selectedChat && chatDetails) {
      setChatDetails(selectedChat);
    }
    if (selectedChat) {
      const hasUnread = (selectedChat?.unreadCounts?.[user.uid] ?? 0) > 0;
      if (hasUnread && counterResetChatIdRef.current !== selectedChat.id) {
        counterResetChatIdRef.current = selectedChat.id;
        setSearchedChats(prev => {
          const newChats = [...prev];
          const chatIndex = newChats.findIndex(chat => chat.id === selectedChat.id);
          if (chatIndex !== -1) {
            newChats[chatIndex] = {
              ...selectedChat!,
              unreadCount: 0,
              unreadCounts: { ...selectedChat?.unreadCounts, [user.uid]: 0 },
            };
          }
          return newChats;
        });
        // Only resets the chat's counter: `messages` may still hold the previous chat here, and one
        // missing message doc fails the whole server batch. Receipts come from markIncomingAsRead.
        markMessagesAsReadRequest({
          companyId: selectedChat?.companyId!,
          chatId: selectedChat?.id!,
          userId: user.uid,
          messageIds: [],
        })
          .then(() => refreshUnreadCountRef.current())
          .catch((e: any) => console.error('Failed to mark messages as read', e?.message));
      }
    }
  }, [selectedChat]);

  // Messages that arrive while the chat is open are read on the spot — as in the mobile app —
  // otherwise the nav unread badge keeps counting them.
  const markIncomingAsRead = (companyId: string, openChatId: string, chatMessages: ChatMessage[]) => {
    if (document.hidden) {
      return;
    }
    const messageIds = chatMessages
      .filter(
        item =>
          !!item.id &&
          item.senderId !== user.uid &&
          !item.readBy?.includes(user.uid) &&
          !markedAsReadIdsRef.current.has(item.id)
      )
      .map(item => item.id as string);
    if (!messageIds.length) {
      return;
    }
    messageIds.forEach(id => markedAsReadIdsRef.current.add(id));
    setSearchedChats(prev =>
      prev.map(chat =>
        chat.id === openChatId
          ? {
              ...chat,
              hasUnread: false,
              unreadCount: 0,
              unreadCounts: { ...chat.unreadCounts, [user.uid]: 0 },
            }
          : chat
      )
    );
    markMessagesAsReadRequest({ companyId, chatId: openChatId, userId: user.uid, messageIds })
      .then(() => refreshUnreadCountRef.current())
      .catch((e: any) => {
        messageIds.forEach(id => markedAsReadIdsRef.current.delete(id));
        console.error('Failed to mark messages as read', e?.message);
      });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      const snapshot = lastSnapshotRef.current;
      if (!document.hidden && snapshot) {
        markIncomingAsRead(snapshot.companyId, snapshot.chatId, snapshot.messages);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user.uid]);

  useEffect(() => {
    if (!chatId || !chatCompanyId) {
      return;
    }

    setMessageToEdit(null);
    setMessageToDelete(null);
    setMessageToReply(null);
    setMessages([]);
    markedAsReadIdsRef.current = new Set();
    counterResetChatIdRef.current = null;
    lastSnapshotRef.current = null;
    setSnapshotChatId(null);

    let stop: (() => void) | undefined;
    let active = true;

    const setupListener = async () => {
      setLoadingChat(true);
      stop = await listenToMessages(chatCompanyId, chatId, (msgs: ChatMessage[]) => {
        setMessages(previous => withPendingMessages(chatId, withOlderPages(msgs, previous)));
        setSnapshotChatId(chatId);
        if (active) {
          lastSnapshotRef.current = { companyId: chatCompanyId, chatId, messages: msgs };
          markIncomingAsRead(chatCompanyId, chatId, msgs);
        }
      });
      setLoadingChat(false);
    };

    setupListener();

    return () => {
      active = false;
      if (stop) {
        stop();
      }
    };
  }, [chatId, chatCompanyId]);

  useEffect(() => {
    if (selectedReactionMessage?.id) {
      getMessageReactions(selectedReactionMessage.id);
    }
  }, [selectedReactionMessage]);

  return (
    <Box style={{ display: 'flex', flexDirection: 'row', height, overflowY: 'hidden' }}>
      <Box
        style={{
          width: chatListWidth,
          minWidth: chatListWidth,
          overflowY: 'hidden',
          display: chatListDisplay,
          flexDirection: 'column',
          height: '100%',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        {!restrictedMode && (
          <Box className={classes.listHeader}>
            <Box className={classes.searchRow}>
              <ChatSearchField value={search} onChange={handleSearchChange} placeholder="Search chats" />
              {user.uid !== 'vyce' && (
                <Tooltip title="New group chat">
                  <IconButton
                    onClick={handleNewGroupChat}
                    aria-label="New group chat"
                    className={classes.compose}
                  >
                    <FiEdit size={19} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <ChatFolders
              folders={[
                { key: 'all', label: 'All chats' },
                { key: 'unread', label: 'Unread' },
              ]}
              value={unreadOnlyFilter ? 'unread' : 'all'}
              onChange={key => (key === 'unread') !== unreadOnlyFilter && toggleUnreadFilter()}
              disabled={loadingChats || searchingChats}
            />
          </Box>
        )}

        <Box style={{ flex: 1, overflowY: 'auto' }}>
          <ChatList
            isLegend={isLegend}
            selectedChatId={selectedChatId}
            chats={searchedChats}
            loading={loadingChats || searchingChats}
            onChatClick={onChatClick}
            hasMoreChats={restrictedMode ? false : hasMoreChats}
            loadingMoreChats={loadingMoreChats}
            onLoadMore={() => loadMoreChats(user.uid)}
            emptyText={
              search
                ? `No chats match “${search}”`
                : unreadOnlyFilter
                ? "You're all caught up"
                : 'No chats yet'
            }
            footer={
              restrictedMode ? (
                <ChatSubscriptionUpsellCard onActivateClick={onActivateClick} canActivate={canActivate} />
              ) : undefined
            }
          />
        </Box>

        {restrictedMode && isMobile && !selectedChatId && (
          <Box style={{ padding: 12 }}>
            <ChatActivateMessagingBanner onActivateClick={onActivateClick} canActivate={canActivate} />
          </Box>
        )}
      </Box>

      <Box
        className={clsx(classes.content, {
          [classes.contentShift]: showDetails,
        })}
      >
        {loadingChat && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <VyceLoader />
          </Box>
        )}
        {selectedChat && !loadingChat && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'hidden',
            }}
          >
            <ChatHeader
              chat={selectedChat}
              showDetailsButton={!restrictedMode}
              onDetailsClick={onDetailsClick}
              onClose={() => {
                setSelectedChat(null);
                setSelectedChatId(null);
                history.push(`${route}`);
              }}
            />
            <ChatDropZone
              className={classes.conversation}
              accept={MESSAGING_FILE_ACCEPTS}
              onDrop={attachFiles}
              disabled={!showInput}
            >
              <ChatMessageList
                className={classes.thread}
                messages={messages}
                userId={user.uid}
                showSenders={!isPrivate}
                loadMore={() => loadMoreMessages(selectedChat.id, selectedChat.companyId)}
                onEditMessage={message => {
                  setMessageToReply(null);
                  setMessageToEdit(message);
                }}
                onDeleteMessage={message => {
                  setMessageToDelete(message);
                  setOpenDeleteMessageDialog(true);
                }}
                onReplyMessage={message => {
                  setMessageToEdit(null);
                  setMessageToReply(message);
                }}
                onForwardMessage={message => {
                  setMessageToForward(message);
                  setOpenForwardMessageDialog(true);
                }}
                onReaction={(messageId, emoji) =>
                  reactToMessage({
                    chatId: selectedChat.id,
                    messageId,
                    emoji,
                    companyId: selectedChat.companyId,
                  })
                }
                onReactionDetails={message => {
                  setSelectedReactionMessage(message);
                  setOpenReactionsDialog(true);
                }}
                translateMessage={enableTranslation ? translateMessage : undefined}
                emptyState={
                  snapshotChatId === chatId ? (
                    <Typography color="textSecondary" align="center">
                      No messages yet. Say hello!
                    </Typography>
                  ) : undefined
                }
              />
              {showInput && (
                <Box
                  className={clsx(classes.composer, { [classes.composerClearance]: launcherClearance })}
                  style={{ paddingBottom: restrictedMode ? 16 : messageContainerPaddingBottom }}
                >
                  <ChatInput
                    clearMessageToReply={() => setMessageToReply(null)}
                    messageToReply={messageToReply}
                    loading={loadingInput}
                    messageToEdit={messageToEdit}
                    editMessage={(messageId, newText) => editMessage(selectedChat.id, messageId, newText)}
                    cancelEdit={() => setMessageToEdit(null)}
                    sendMessage={sendMessage}
                    documents={documents}
                    images={images}
                    clearDocuments={() => setDocuments([])}
                    clearImages={() => setImages([])}
                    deleteDocument={deleteDocument}
                    deleteImage={deleteImage}
                    onAttach={attachFiles}
                    accept={FILE_INPUT_ACCEPT}
                    speechToText={speechToText}
                    translateDraft={
                      enableTranslation
                        ? (text, target) => translateDraft(text, target, workerLanguageSamples)
                        : undefined
                    }
                    translationStorageKey={selectedChat.id}
                  />
                  {restrictedMode && (
                    <ChatActivateMessagingBanner
                      onActivateClick={onActivateClick}
                      canActivate={canActivate}
                    />
                  )}
                </Box>
              )}
            </ChatDropZone>
          </Box>
        )}

        {restrictedMode && !isMobile && !selectedChat && !loadingChat && !loadingChats && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gridGap={24}
            height="100%"
            style={{ padding: '0 24px' }}
          >
            <Typography color="textSecondary">No notifications yet</Typography>
            <ChatActivateMessagingBanner onActivateClick={onActivateClick} canActivate={canActivate} />
          </Box>
        )}

        {showDetails && (
          <Drawer
            variant="persistent"
            anchor="right"
            open={showDetails}
            className={classes.drawer}
            classes={{
              paper: classes.drawerPaper,
            }}
          >
            {selectedUser && (
              <ChatUserDetails
                name={selectedUser.name}
                avatarUrl={selectedUser.avatarUrl || ''}
                email={selectedUser.email}
                phone={selectedUser.phone || ''}
                onClose={() => {
                  setSelectedUser(null);
                  setChatDetails(null);
                }}
              />
            )}
            {chatDetails && (
              <ChatDetails
                userId={user.uid}
                isChatAdmin={isChatAdmin}
                chat={chatDetails}
                loadingChatUpdate={loadingChatUpdate}
                onClose={() => setChatDetails(null)}
                updateChatName={updateChatName}
                updateChatAvatar={updateChatAvatar}
                updateChatReadOnlyStatus={updateChatReadOnlyStatus}
                openAddMembersDialog={() => setOpenAddMembersDialog(true)}
                removeUserFromChat={removeUserFromChat}
                goToChat={goToChat}
              />
            )}
          </Drawer>
        )}

        <ChatNewGroupChatDialog
          open={openNewGroupChatDialog}
          handleClose={() => setOpenNewGroupChatDialog(false)}
          createGroupChat={createGroupChat}
          searchChatUsers={searchChatUsers}
        />

        <ChatAddMembersDialog
          open={openAddMembersDialog}
          handleClose={() => setOpenAddMembersDialog(false)}
          searchChatUsers={searchChatUsers}
          existingMembers={selectedChat?.profiles || []}
          addUsersToChat={addUsersToChat}
        />

        <ConfirmDialog
          open={openDeleteMessageDialog}
          title="Do you want to delete this message?"
          subtitle="This action cannot be undone."
          handleClose={() => setOpenDeleteMessageDialog(false)}
          confirmText="Delete message"
          cancelText="Cancel"
          handleConfirm={() => {
            if (messageToDelete?.id && selectedChat?.id) {
              deleteMessage(selectedChat.id, messageToDelete.id);
              setOpenDeleteMessageDialog(false);
              setMessageToDelete(null);
            }
          }}
        />

        <ChatForwardMessageDialog
          open={openForwardMessageDialog}
          handleClose={() => setOpenForwardMessageDialog(false)}
          chats={chats.filter(chat => !chat.readOnly)}
          forwardMessage={forwardMessage}
        />

        <ChatMessageReactionsDialog
          open={openReactionsDialog}
          handleClose={() => {
            setOpenReactionsDialog(false);
            setSelectedReactionMessage(null);
          }}
          message={selectedReactionMessage}
          loading={loadingMessageReactions}
          reactions={messageReactions}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  listHeader: {
    flex: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '12px 12px 8px',
  },
  searchRow: { display: 'flex', alignItems: 'center', gap: 6 },
  compose: { flex: 'none', color: theme.palette.text.secondary },
  // Messaging keeps its tiled construction wall behind the thread and the
  // composer, so it doesn't look like a Laila chat (Laila keeps the dots).
  conversation: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    backgroundImage: `url(${
      theme.palette.type === 'dark' ? messagesDarkBackground : messagesLightBackground
    })`,
    backgroundSize: '110px 110px',
    backgroundPosition: 'center',
    backgroundRepeat: 'repeat',
  },
  thread: { flex: 1, background: 'none' },
  composer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    maxWidth: 792,
    margin: '0 auto',
    padding: '4px 16px 16px',
    [theme.breakpoints.down('xs')]: { padding: '4px 8px 8px' },
  },
  // The orb is 52px, 24px from the corner (16px on phones).
  composerClearance: {
    maxWidth: 792 + 72,
    paddingRight: 88,
    [theme.breakpoints.down('xs')]: { paddingRight: 76 },
  },
  drawer: {
    width: leftInfoDrawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: leftInfoDrawerWidth,
    marginTop: headerHeight,
    paddingBottom: headerHeight + bottomButtonHeight,
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      marginTop: 0,
      paddingBottom: bottomButtonHeight,
    },
  },
  content: {
    flexGrow: 1,
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: 0,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      minHeight: '100%',
      height: 'auto',
    },
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: leftInfoDrawerWidth,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
}));
